import { UniDriver } from '@wix/unidriver-core';
import { BaseUniDriver } from '@wix/wix-ui-test-utils/unidriver';
import {
  baseUniDriverFactory,
  isElementFocused,
} from '../utils/test-utils/unidriver';
import { dataHooks } from './AngleInput.constants';

export interface AngleInputUniDriver extends BaseUniDriver {
  isFocused(): Promise<boolean | undefined>;
  isDisabled(): Promise<boolean>;
  focus(): Promise<void>;
  blur(): Promise<void>;
  getValue(): Promise<string>;
  setValue(value: string): Promise<void>;
  incrementByStepCount(times: number): Promise<void>;
  decrementByStepCount(times: number): Promise<void>;
}

export const angleInputDriverFactory = (
  base: UniDriver,
): AngleInputUniDriver => {
  const getElementByHook = (hook: string) => base.$(`[data-hook=${hook}]`);
  const input = getElementByHook(dataHooks.input);

  return {
    ...baseUniDriverFactory(base),

    /**
     * Checks if the input element is focused.
     */
    isFocused: async () => await isElementFocused(input),

    /**
     * Checks if the input element is disabled.
     */
    isDisabled: async () => {
      const disabledAttr = await input.attr('disabled');
      return disabledAttr !== null;
    },

    /**
     * Focuses the input element.
     */

    focus: async () => (await input.getNative()).focus(),

    /**
     * Blurs the input element.
     */
    blur: async () => (await input.getNative()).blur(),

    /**
     * Retrieves the value of the input element.
     */
    getValue: async () => await input.value(),

    /**
     * Sets the value of the input element.
     */
    setValue: async value => await input.enterValue(value),

    /**
     * Increases the angle by pressing the 'ArrowUp' key a specified number of times.
     */
    incrementByStepCount: async (times: number) => {
      (await input.getNative()).focus();
      for (let i = 0; i < times; i++) {
        await input.pressKey('ArrowUp');
      }
    },

    /**
     * Decreases the angle by pressing the 'ArrowDown' key a specified number of times.
     */
    decrementByStepCount: async (times: number) => {
      (await input.getNative()).focus();
      for (let i = 0; i < times; i++) {
        await input.pressKey('ArrowDown');
      }
    },
  };
};
